// Enhanced authentication service with bot detection and role-based access
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { 
  performBotDetection, 
  trackLoginAttempt, 
  blockUserIfBot,
  shouldChallengeUser,
  verifyCaptcha
} from './botDetectionService';

// User roles and permissions
export const USER_ROLES = {
  GUEST: 'guest',
  PENDING: 'pending',
  MEMBER: 'member',
  VIP_MEMBER: 'vip_member',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
  CHAPTER_PRESIDENT: 'chapter_president',
  AMBASSADOR: 'ambassador'
};

export const ROLE_PERMISSIONS = {
  [USER_ROLES.GUEST]: [
    'view_public_content',
    'register_account'
  ],
  [USER_ROLES.PENDING]: [
    'view_public_content',
    'view_pending_status'
  ],
  [USER_ROLES.MEMBER]: [
    'view_public_content',
    'view_member_content',
    'access_member_directory',
    'join_groups',
    'post_in_groups',
    'register_for_events',
    'apply_to_jobs',
    'apply_to_projects'
  ],
  [USER_ROLES.VIP_MEMBER]: [
    'view_public_content',
    'view_member_content',
    'view_vip_content',
    'access_member_directory',
    'join_groups',
    'post_in_groups',
    'create_groups',
    'register_for_events',
    'apply_to_jobs',
    'apply_to_projects',
    'post_jobs',
    'post_projects'
  ],
  [USER_ROLES.CHAPTER_PRESIDENT]: [
    'view_public_content',
    'view_member_content',
    'view_vip_content',
    'access_member_directory',
    'join_groups',
    'post_in_groups',
    'create_groups',
    'moderate_groups',
    'register_for_events',
    'create_events',
    'manage_chapter_events',
    'apply_to_jobs',
    'apply_to_projects',
    'post_jobs',
    'post_projects'
  ],
  [USER_ROLES.ADMIN]: [
    'view_public_content',
    'view_member_content',
    'view_vip_content',
    'view_admin_content',
    'access_member_directory',
    'manage_users',
    'approve_members',
    'join_groups',
    'post_in_groups',
    'create_groups',
    'moderate_groups',
    'delete_groups',
    'register_for_events',
    'create_events',
    'manage_all_events',
    'apply_to_jobs',
    'apply_to_projects',
    'post_jobs',
    'post_projects',
    'moderate_content',
    'access_analytics'
  ],
  [USER_ROLES.SUPER_ADMIN]: [
    'all_permissions'
  ]
};

// Enhanced login with bot detection
export const enhancedLogin = async (email, password, captchaResponse = null) => {
  try {
    // Perform bot detection
    const botDetection = await performBotDetection();
    
    if (botDetection.isBot) {
      throw new Error('Automated access detected. Please verify you are human.');
    }

    // Check if CAPTCHA is required for suspicious activity
    if (botDetection.botScore > 30 && !captchaResponse) {
      throw new Error('Additional verification required. Please complete the CAPTCHA.');
    }

    // Verify CAPTCHA if provided
    if (captchaResponse) {
      const captchaResult = await verifyCaptcha(captchaResponse);
      if (!captchaResult.success) {
        throw new Error('CAPTCHA verification failed. Please try again.');
      }
    }

    // Attempt login
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Check if user is blocked
    await blockUserIfBot(user.uid);

    // Get user profile
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : null;

    // Check if user is approved
    if (!userData || userData.status !== 'approved') {
      await signOut(auth);
      throw new Error('Account pending approval or suspended.');
    }

    // Check for bot flags in user data
    if (userData.botDetection?.flagged) {
      await signOut(auth);
      throw new Error('Account has been flagged for suspicious activity.');
    }

    // Track successful login
    trackLoginAttempt(user.uid, true, { 
      botScore: botDetection.botScore,
      captchaUsed: !!captchaResponse 
    });

    // Update last login
    await updateDoc(doc(db, 'users', user.uid), {
      lastLogin: serverTimestamp(),
      loginCount: (userData.loginCount || 0) + 1
    });

    return {
      user,
      userData,
      requiresAdditionalVerification: shouldChallengeUser(user.uid)
    };

  } catch (error) {
    // Track failed login attempt
    if (error.code !== 'auth/user-not-found') {
      const tempUserId = `temp_${email.replace('@', '_').replace('.', '_')}`;
      trackLoginAttempt(tempUserId, false, { 
        error: error.message,
        email 
      });
    }
    throw error;
  }
};

// Enhanced registration with bot detection
export const enhancedRegister = async (userData, captchaResponse) => {
  try {
    // Perform bot detection
    const botDetection = await performBotDetection();

    // For now, only block obvious bots (score > 80) to allow legitimate users
    if (botDetection.isBot && botDetection.botScore > 80) {
      throw new Error('Automated registration detected. Please verify you are human.');
    }

    // Verify CAPTCHA if provided
    if (captchaResponse) {
      const captchaResult = await verifyCaptcha(captchaResponse);
      if (!captchaResult.success) {
        throw new Error('CAPTCHA verification failed. Please try again.');
      }
    }

    // Validate email exists
    if (!userData.email || typeof userData.email !== 'string') {
      throw new Error('Valid email is required for registration.');
    }

    // Normalize email
    const normalizedEmail = userData.email.trim().toLowerCase();
    console.log('🔍 Registration attempt for email:', normalizedEmail);

    // Check for existing users with same email
    const existingUserQuery = query(
      collection(db, 'users'),
      where('email', '==', normalizedEmail)
    );
    const existingUsers = await getDocs(existingUserQuery);
    console.log('🔍 Firestore check - existing users found:', existingUsers.size);

    if (!existingUsers.empty) {
      console.log('❌ User exists in Firestore');
      throw new Error('An account with this email already exists.');
    }

    console.log('✅ Email not found in Firestore, proceeding to Firebase Auth...');

    // Create user account - handle case where Firebase Auth user exists but Firestore doc doesn't
    let userCredential;
    try {
      console.log('🔍 Attempting to create Firebase Auth user...');
      userCredential = await createUserWithEmailAndPassword(
        auth,
        normalizedEmail,
        userData.password
      );
      console.log('✅ Firebase Auth user created successfully');
    } catch (error) {
      console.log('❌ Firebase Auth error:', error.code, error.message);
      if (error.code === 'auth/email-already-in-use') {
        // Check if this is an orphaned auth user (exists in Auth but not in Firestore)
        if (existingUsers.empty) {
          console.log('🔍 Orphaned auth user detected - exists in Auth but not Firestore');
          console.log('🔧 Attempting to sign in to existing auth user and create Firestore profile...');

          try {
            // Sign in to the existing auth user
            const existingUserCredential = await signInWithEmailAndPassword(
              auth,
              normalizedEmail,
              userData.password
            );
            console.log('✅ Successfully signed in to existing auth user');
            userCredential = existingUserCredential;
          } catch (signInError) {
            console.log('❌ Failed to sign in to existing auth user:', signInError.message);
            throw new Error(`This email exists in Firebase Auth but the password doesn't match. Please try logging in or use password reset. If you believe this is an error, please contact support.`);
          }
        } else {
          console.log('🔍 User exists in both Auth and Firestore');
          throw new Error('An account with this email already exists. Please try logging in instead.');
        }
      } else {
        throw error;
      }
    }
    const user = userCredential.user;

    // Update user profile
    await updateProfile(user, {
      displayName: `${userData.firstName} ${userData.lastName}`
    });

    // Sanitize bot detection data to remove undefined values
    const sanitizeBotDetectionData = (data) => {
      if (!data || typeof data !== 'object') return {};

      const sanitized = {};
      for (const [key, value] of Object.entries(data)) {
        if (value !== undefined && value !== null) {
          if (typeof value === 'object' && !Array.isArray(value)) {
            sanitized[key] = sanitizeBotDetectionData(value);
          } else {
            sanitized[key] = value;
          }
        }
      }
      return sanitized;
    };

    // Determine if user should be auto-approved (if they have a valid discount code or will pay)
    const shouldAutoApprove = userData.discountCode && userData.discountCode.trim();

    // Create user document with enhanced security
    const userDocData = {
      ...userData,
      uid: user.uid,
      email: normalizedEmail,
      role: shouldAutoApprove ? USER_ROLES.MEMBER : USER_ROLES.PENDING,
      status: shouldAutoApprove ? 'approved' : 'pending',
      memberSince: shouldAutoApprove ? serverTimestamp() : null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      loginCount: 0,
      botDetection: {
        registrationBotScore: botDetection.botScore || 0,
        registrationChecks: sanitizeBotDetectionData(botDetection.checks || {}),
        flagged: false
      },
      securityFlags: {
        requiresAdditionalVerification: (botDetection.botScore || 0) > 30,
        lastSecurityCheck: serverTimestamp()
      }
    };

    // Remove password from stored data
    delete userDocData.password;

    await setDoc(doc(db, 'users', user.uid), userDocData);

    // Notify admins about new user registration (only if needs approval)
    if (!shouldAutoApprove) {
      try {
        const { notifyAdminsNewUser } = await import('./notificationService');
        await notifyAdminsNewUser(
          userData.displayName || `${userData.firstName} ${userData.lastName}`,
          normalizedEmail
        );
      } catch (error) {
        console.error('Error notifying admins about new user:', error);
        // Don't fail registration if notification fails
      }
    }

    // Only sign out user if they need approval
    if (!shouldAutoApprove) {
      await signOut(auth);
    }

    return {
      success: true,
      message: shouldAutoApprove
        ? 'Registration successful! Welcome to APN!'
        : 'Registration successful! Your account is pending approval.',
      userId: user.uid,
      autoApproved: shouldAutoApprove
    };

  } catch (error) {
    throw error;
  }
};

// Check user permissions
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;
  
  const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
  
  // Super admin has all permissions
  if (rolePermissions.includes('all_permissions')) return true;
  
  return rolePermissions.includes(permission);
};

// Check multiple permissions
export const hasAnyPermission = (userRole, permissions) => {
  return permissions.some(permission => hasPermission(userRole, permission));
};

// Check if user has required role
export const hasRole = (userRole, requiredRole) => {
  const roleHierarchy = {
    [USER_ROLES.GUEST]: 0,
    [USER_ROLES.PENDING]: 1,
    [USER_ROLES.MEMBER]: 2,
    [USER_ROLES.VIP_MEMBER]: 3,
    [USER_ROLES.CHAPTER_PRESIDENT]: 4,
    [USER_ROLES.ADMIN]: 5,
    [USER_ROLES.SUPER_ADMIN]: 6
  };

  const userLevel = roleHierarchy[userRole] || 0;
  const requiredLevel = roleHierarchy[requiredRole] || 0;

  return userLevel >= requiredLevel;
};

// Enhanced logout with cleanup
export const enhancedLogout = async () => {
  try {
    await signOut(auth);
    
    // Clear any cached data
    sessionStorage.clear();
    
    // Clear any bot detection tracking for this session
    // (This would be handled by the bot detection service)
    
    return { success: true };
  } catch (error) {
    throw error;
  }
};

// Verify user session and check for security issues
export const verifyUserSession = async (user) => {
  try {
    if (!user) return { valid: false, reason: 'No user session' };

    // Get current user data
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : null;

    if (!userData) {
      return { valid: false, reason: 'User data not found' };
    }

    // Check if user is still approved
    if (userData.status !== 'approved') {
      return { valid: false, reason: 'Account no longer approved' };
    }

    // Check for bot flags
    if (userData.botDetection?.flagged) {
      return { valid: false, reason: 'Account flagged for suspicious activity' };
    }

    // Check if additional verification is required
    const requiresVerification = shouldChallengeUser(user.uid);

    return {
      valid: true,
      userData,
      requiresAdditionalVerification: requiresVerification
    };

  } catch (error) {
    return { valid: false, reason: 'Session verification failed' };
  }
};

// Update user security status
export const updateUserSecurityStatus = async (userId, securityData) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      'securityFlags.lastSecurityCheck': serverTimestamp(),
      'securityFlags.requiresAdditionalVerification': securityData.requiresVerification || false,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user security status:', error);
  }
};

export default {
  enhancedLogin,
  enhancedRegister,
  enhancedLogout,
  hasPermission,
  hasAnyPermission,
  hasRole,
  verifyUserSession,
  updateUserSecurityStatus,
  USER_ROLES,
  ROLE_PERMISSIONS
};
